import {
  DashboardCalendarType,
  DashboardMeetingView,
  DashboardMeetingPerson,
  MeetingOrigin,
} from "@/interfaces/DashboardMeetingStats";
import { useGetDashboardMeetingDetailsQuery } from "@/services/admin/dashboardMeetingStats";
import { DateTime } from "luxon";
import { FC, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import MeetingDetailsTable from "./MeetingDetailsTable";
import "./MeetingStatsSection.scss";

const PARIS = "Europe/Paris";
const FORMAT = "yyyy-MM-dd'T'HH:mm";
const VIEWS = {
  scheduled: {
    label: "Prévus sur la période",
    description: "Selon la date et l’heure du rendez-vous.",
  },
  booked: {
    label: "Pris sur la période",
    description: "Selon la date et l’heure de sa création.",
  },
};

const includeSelected = (
  people: DashboardMeetingPerson[] | undefined,
  selected: DashboardMeetingPerson | null,
) =>
  selected && !people?.some((person) => person.id === selected.id)
    ? [...(people ?? []), selected]
    : (people ?? []);

const MeetingStatsSection: FC = () => {
  const [startAt, setStartAt] = useState(() =>
    DateTime.now().setZone(PARIS).startOf("day").toFormat(FORMAT),
  );
  const [endAt, setEndAt] = useState(() =>
    DateTime.now().setZone(PARIS).endOf("day").toFormat(FORMAT),
  );
  const [view, setView] = useState<DashboardMeetingView>("scheduled");
  const [search, setSearch] = useState("");
  const [author, setAuthor] = useState<DashboardMeetingPerson | null>(null);
  const [owner, setOwner] = useState<DashboardMeetingPerson | null>(null);
  const authorId = author?.id ?? "";
  const ownerId = owner?.id ?? "";
  const [calendarType, setCalendarType] = useState<DashboardCalendarType | "">(
    "",
  );
  const [page, setPage] = useState(1);
  const [origin, setOrigin] = useState<MeetingOrigin | "">("");
  const start = DateTime.fromFormat(startAt, FORMAT, { zone: PARIS });
  const end = DateTime.fromFormat(endAt, FORMAT, { zone: PARIS });
  const validRange =
    start.isValid &&
    end.isValid &&
    start <= end &&
    start.toFormat(FORMAT) === startAt &&
    end.toFormat(FORMAT) === endAt;

  const {
    currentData,
    data: previousData,
    isFetching,
    isError,
    refetch,
  } = useGetDashboardMeetingDetailsQuery(
    {
      startAt,
      endAt,
      view,
      page,
      query: search.trim() || undefined,
      authorId: authorId || undefined,
      ownerId: ownerId || undefined,
      calendarType: calendarType || undefined,
      origin: origin || undefined,
    },
    {
      skip: !validRange,
      pollingInterval: 30000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  // Keep selector labels while loading, but never show rows/counts for previous filters.
  const options = (currentData ?? previousData)?.options;
  const authors = includeSelected(options?.authors, author);
  const owners = includeSelected(options?.owners, owner);
  // Keep the current filter's results mounted during polling/socket refreshes.
  // currentData is absent when new filters have no result yet.
  const report = validRange && !isError ? currentData : undefined;
  const pagination = report?.pagination;
  const update = <T,>(setter: (value: T) => void, value: T) => {
    setter(value);
    setPage(1);
  };
  const resetFilters = () => {
    setSearch("");
    setAuthor(null);
    setOwner(null);
    setCalendarType("");
    setOrigin("");
    setPage(1);
  };

  return (
    <section className="meeting-report" aria-label="Analyse des rendez-vous">
      <Row className="g-3 mb-3 align-items-end">
        <Col md={6}>
          <h2 className="h6 mb-1">Période analysée</h2>
          <p className="meeting-report-note mb-0">Dates et heures de Paris</p>
        </Col>
        <Col md={3}>
          <Form.Group controlId="meeting-period-start">
            <Form.Label>Du</Form.Label>
            <Form.Control
              type="datetime-local"
              required
              value={startAt}
              onChange={(e) => update(setStartAt, e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="meeting-period-end">
            <Form.Label>Au</Form.Label>
            <Form.Control
              type="datetime-local"
              required
              value={endAt}
              onChange={(e) => update(setEndAt, e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>
      {!validRange && (
        <Alert variant="warning">
          Choisissez des dates et heures valides à Paris ; la fin de période
          doit être après le début ou égale à celui-ci.
        </Alert>
      )}

      <Row className="g-3 mb-3">
        {(Object.keys(VIEWS) as DashboardMeetingView[]).map((key) => (
          <Col md={6} key={key}>
            <button
              type="button"
              className="meeting-report-mode"
              aria-pressed={view === key}
              onClick={() => update(setView, key)}
            >
              <span>
                <strong>{VIEWS[key].label}</strong>
                <span className="meeting-report-note d-block mt-1">
                  {VIEWS[key].description}
                </span>
              </span>
              <span className="meeting-report-count">
                {report ? report.counts[key].toLocaleString("fr-FR") : "—"}
              </span>
            </button>
          </Col>
        ))}
      </Row>

      <Card>
        <Card.Body className="pb-0">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
            <div>
              <h2 className="h5 mb-1">
                {view === "scheduled"
                  ? "Rendez-vous prévus sur la période"
                  : "Rendez-vous pris sur la période"}
              </h2>
              <p className="meeting-report-note mb-0">
                {view === "scheduled"
                  ? "Retrouvez qui les a pris et qui va les assurer."
                  : "Consultez les prises de rendez-vous, même pour une date ultérieure."}
              </p>
            </div>
            <Button variant="link" size="sm" onClick={resetFilters}>
              Réinitialiser les filtres
            </Button>
          </div>
          <Row className="g-3 mb-3">
            <Col md={6} xl={4}>
              <Form.Group controlId="meeting-client-search">
                <Form.Label>Client ou titre du rendez-vous</Form.Label>
                <Form.Control
                  type="search"
                  placeholder="Rechercher un client…"
                  maxLength={200}
                  value={search}
                  onChange={(e) => update(setSearch, e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6} xl={3}>
              <Form.Group controlId="meeting-author-filter">
                <Form.Label>Pris par</Form.Label>
                <Form.Select
                  value={authorId}
                  onChange={(e) =>
                    update(
                      setAuthor,
                      authors.find((person) => person.id === e.target.value) ??
                        null,
                    )
                  }
                >
                  <option value="">Toutes les personnes</option>
                  {authors.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.fullname}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} xl={3}>
              <Form.Group controlId="meeting-owner-filter">
                <Form.Label>Assuré par</Form.Label>
                <Form.Select
                  value={ownerId}
                  onChange={(e) =>
                    update(
                      setOwner,
                      owners.find((person) => person.id === e.target.value) ??
                        null,
                    )
                  }
                >
                  <option value="">Toutes les personnes</option>
                  {owners.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.fullname}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} xl={2}>
              <Form.Group controlId="meeting-calendar-filter">
                <Form.Label>Calendrier</Form.Label>
                <Form.Select
                  value={calendarType}
                  onChange={(e) =>
                    update(
                      setCalendarType,
                      e.target.value as DashboardCalendarType | "",
                    )
                  }
                >
                  <option value="">Tous les calendriers</option>
                  <option value="commercial">Commercial</option>
                  <option value="montage">Montage</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex align-items-end gap-4 flex-wrap mb-3">
            <Form.Group controlId="meeting-origin-filter">
              <Form.Label>Origine</Form.Label>
              <Form.Select
                value={origin}
                onChange={(e) =>
                  update(setOrigin, e.target.value as MeetingOrigin | "")
                }
              >
                <option value="">Tous les rendez-vous</option>
                <option value="linked">Liés à un appel</option>
                <option value="unlinked">Sans appel associé</option>
              </Form.Select>
            </Form.Group>
            {report?.attributionCounts && (
              <div
                className="d-flex gap-4 pb-2 flex-wrap"
                aria-label="Origine des rendez-vous"
              >
                <span>
                  {report.attributionCounts.linked.toLocaleString("fr-FR")} liés
                  à un appel
                </span>
                <span>
                  {report.attributionCounts.unlinked.toLocaleString("fr-FR")}{" "}
                  sans appel associé
                </span>
              </div>
            )}
          </div>
          {!!report?.byAuthor.length && (
            <div
              className="meeting-report-authors mb-3"
              aria-label="Nombre de rendez-vous par auteur"
            >
              <span className="meeting-report-note">Pris par</span>
              {report.byAuthor.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  aria-pressed={authorId === person.id}
                  onClick={() => update(setAuthor, person)}
                >
                  {person.fullname}{" "}
                  <strong>· {person.count.toLocaleString("fr-FR")}</strong>
                </button>
              ))}
            </div>
          )}
        </Card.Body>

        {validRange && isError ? (
          <Alert variant="danger" className="m-3">
            Impossible de charger les rendez-vous.{" "}
            <Button variant="link" onClick={() => refetch()}>
              Réessayer
            </Button>
          </Alert>
        ) : validRange && isFetching && !report ? (
          <div role="status" className="text-center p-5">
            <Spinner animation="border" size="sm" className="me-2" />
            Chargement des rendez-vous…
          </div>
        ) : report ? (
          <MeetingDetailsTable rows={report.rows} view={view} />
        ) : null}

        {pagination && (
          <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="meeting-report-note">
              {pagination.total.toLocaleString("fr-FR")} rendez-vous
            </span>
            <nav
              aria-label="Pagination des rendez-vous"
              className="d-flex align-items-center gap-3"
            >
              <Button
                variant="outline-primary"
                size="sm"
                aria-label="Page précédente"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Précédent
              </Button>
              <span className="meeting-report-note">
                Page {page} sur {pagination.lastPage}
              </span>
              <Button
                variant="outline-primary"
                size="sm"
                aria-label="Page suivante"
                disabled={page >= pagination.lastPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </nav>
          </Card.Footer>
        )}
      </Card>
      <p className="meeting-report-note mt-3 mb-1">
        Un rendez-vous peut figurer dans les deux vues si sa création et sa
        tenue se situent dans la période sélectionnée.
      </p>
      <p className="meeting-report-note mb-0">
        « Assuré par » indique l’affectation actuelle. L’auteur de certains
        anciens rendez-vous n’est pas renseigné.
      </p>
      <p className="meeting-report-note mt-1 mb-0">
        Rattachement automatique selon les horaires enregistrés : dernier appel
        décroché du client, pendant l’appel ou dans les 24 h après sa fin.
        L’appel peut être antérieur à la période affichée. « Sans appel associé
        » ne signifie pas qu’aucun échange n’a eu lieu.
      </p>
    </section>
  );
};

export default MeetingStatsSection;
